import { useEffect, useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { inr } from "@/theme";
import { useAdminTheme } from "@/admin/AdminTheme";
import { ACard, AButton, AInput, Badge, AdminPageTitle } from "@/admin/ui";
import { Spinner } from "@/components/common";
import { toast } from "sonner";

const emptyCoupon = { code: "", kind: "percent", value: 10, min_order: 0, max_discount: 0, active: true, expires_at: "" };

export default function AdminPromotions() {
  const { c } = useAdminTheme();
  const [coupons, setCoupons] = useState(null);
  const [form, setForm] = useState(emptyCoupon);
  const [busy, setBusy] = useState(false);

  const load = () => api("/admin/coupons").then(setCoupons).catch(() => setCoupons([]));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.code.trim()) return toast("Enter a coupon code");
    setBusy(true);
    try {
      await api("/admin/coupons", { method: "POST", body: { ...form, value: Number(form.value), min_order: Number(form.min_order), max_discount: Number(form.max_discount) } });
      toast.success("Coupon saved"); setForm(emptyCoupon); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const del = async (code) => {
    if (!window.confirm(`Delete ${code}?`)) return;
    try { await api(`/admin/coupons/${code}`, { method: "DELETE" }); toast.success("Deleted"); load(); } catch (e) { toast.error(e.message); }
  };

  if (!coupons) return <Spinner full />;

  return (
    <div className="fade-up" style={{ maxWidth: 900 }}>
      <AdminPageTitle title="Promotions" subtitle="Coupons & discounts" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px,1fr))", gap: 16 }}>
        <ACard>
          <h3 className="font-display" style={{ margin: "0 0 14px", fontSize: 18, fontWeight: 700 }}>New Coupon</h3>
          <div style={{ display: "grid", gap: 12 }}>
            <AInput label="Code" data-testid="coupon-code" value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="WELCOME10" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <label style={{ display: "block" }}>
                <span style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: c.mutedText, fontWeight: 700 }}>Kind</span>
                <select data-testid="coupon-kind" value={form.kind} onChange={(e) => set("kind", e.target.value)} style={{ width: "100%", marginTop: 6, background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10, padding: "11px 14px", color: c.onSurface, fontFamily: "DM Sans" }}>
                  <option value="percent">Percent %</option>
                  <option value="flat">Flat ₹</option>
                </select>
              </label>
              <AInput label={form.kind === "percent" ? "Percent" : "Amount ₹"} type="number" data-testid="coupon-value" value={form.value} onChange={(e) => set("value", e.target.value)} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <AInput label="Min Order ₹" type="number" value={form.min_order} onChange={(e) => set("min_order", e.target.value)} />
              <AInput label="Max Discount ₹" type="number" value={form.max_discount} onChange={(e) => set("max_discount", e.target.value)} />
            </div>
            <AButton data-testid="coupon-create" onClick={create} disabled={busy}><Plus size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />{busy ? "Saving…" : "Create Coupon"}</AButton>
          </div>
        </ACard>

        <ACard style={{ padding: 0, overflow: "hidden" }}>
          <h3 className="font-display" style={{ margin: 0, fontSize: 18, fontWeight: 700, padding: 20 }}>Active Coupons</h3>
          {coupons.length === 0 ? <p style={{ padding: "0 20px 20px", color: c.mutedText }}>No coupons yet.</p> : coupons.map((cp) => (
            <div key={cp.code} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", borderTop: `1px solid ${c.divider}` }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontFamily: "monospace" }}>{cp.code}</div>
                <div style={{ fontSize: 12.5, color: c.mutedText }}>{cp.kind === "percent" ? `${cp.value}% off` : `${inr(cp.value)} off`}{cp.min_order ? ` · min ${inr(cp.min_order)}` : ""}</div>
              </div>
              <Badge tone={cp.active ? "success" : "danger"}>{cp.active ? "Active" : "Off"}</Badge>
              <AButton variant="ghost" style={{ padding: 8, color: c.danger }} data-testid={`coupon-delete-${cp.code}`} onClick={() => del(cp.code)}><Trash2 size={15} /></AButton>
            </div>
          ))}
        </ACard>
      </div>
    </div>
  );
}
